import { useCallback, useEffect, useRef, useState } from 'react'
import { FileClock, Trash2, Save, Loader2, ChevronDown } from 'lucide-react'
import api from '../../services/api'
import { formatDate, parseServerDate } from '../../utils/helpers'

/**
 * Save this, come back to it, keep several on the go.
 *
 * Replaces a single browser-local slot that held ONE draft: starting a second project silently
 * discarded the first, and a draft begun on a laptop did not exist on a phone.
 *
 * One component for both the project form and the deliverable form, on purpose. Two
 * implementations of "save my work" would drift, and the half that drifted would be the one
 * somebody was relying on.
 *
 * Props:
 *   kind        'project' | 'deliverable'
 *   collect()   returns the plain-JSON snapshot to store
 *   apply(p)    puts a snapshot back into the parent's form
 *   label       what to call the current draft (usually the title being typed)
 *   context     { project_id, milestone_id } — what this draft belongs to
 *   filesWarning  true for deliverables, where attachments are not saved
 *   registerSave  optional — receives this component's save function, so a button elsewhere
 *                 on the page (the wizard footer) can trigger the SAME save. Two buttons
 *                 saying "Save Draft" that did different things is what this replaces.
 */
export default function DraftBar({ kind, collect, apply, label, context = {}, filesWarning = false,
                                  registerSave, onSaved }) {
  const [drafts, setDrafts] = useState([])
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const [note, setNote] = useState(null)
  // Which draft is being edited right now, so saving again updates it instead of piling up a
  // new row per keystroke-batch.
  const currentId = useRef(null)

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/drafts/', { params: { kind } })
      setDrafts(data.items || [])
    } catch { /* a drafts list that will not load must not block the form behind it */ }
  }, [kind])

  useEffect(() => { load() }, [load])

  const save = async () => {
    setBusy(true); setNote(null)
    try {
      const { data } = await api.post('/drafts/', {
        kind,
        label: (label || '').trim() || 'Untitled',
        payload: collect() || {},
        project_id: context.project_id ?? null,
        milestone_id: context.milestone_id ?? null,
      }, { params: currentId.current ? { draft_id: currentId.current } : {} })
      currentId.current = data.id
      // The parent parks any real files against this id before we report success, so "Saved"
      // never appears while the attachments are still only in memory.
      if (onSaved) await onSaved(data.id)
      setNote({ kind: 'ok', text: `Saved as "${data.label}".` })
      await load()
    } catch (e) {
      setNote({ kind: 'error', text: e.response?.data?.detail || 'Could not save the draft.' })
    } finally { setBusy(false) }
  }

  const resume = (d) => {
    apply(d.payload || {}, d.id)
    currentId.current = d.id          // continue THIS draft rather than spawning another
    setOpen(false)
    setNote({
      kind: 'ok',
      text: filesWarning
        ? `Continuing "${d.label}". Files you attached on this device are back.`
        : `Continuing "${d.label}".`,
    })
  }

  const remove = async (d) => {
    if (!window.confirm(`Delete the draft "${d.label}"? This cannot be undone.`)) return
    try {
      await api.delete(`/drafts/${d.id}`)
      if (currentId.current === d.id) currentId.current = null
      await load()
    } catch (e) {
      setNote({ kind: 'error', text: e.response?.data?.detail || 'Could not delete it.' })
    }
  }

  // Hand `save` up so the footer button runs exactly this, not a second implementation.
  // Re-registered whenever save changes identity, or the footer would call a stale closure
  // holding the form state from first render.
  useEffect(() => {
    if (registerSave) registerSave(save)
  })

  const when = (iso) => {
    if (!iso) return ''
    // parseServerDate, not new Date(). The server sends a naive timestamp with no zone, and
    // new Date() reads that as LOCAL — so in India a draft saved seconds ago reported "6 h ago",
    // and somebody deciding which of two drafts is the newer one was being told the wrong thing.
    const d = parseServerDate(iso)
    if (!d) return ''
    const mins = Math.round((Date.now() - d.getTime()) / 60000)
    if (mins < 1) return 'just now'
    if (mins < 60) return `${mins} min ago`
    if (mins < 1440) return `${Math.round(mins / 60)} h ago`
    // The project-wide format (07-Jun-2026), not the browser's. toLocaleDateString gave
    // "8/24/2026" here — month-first, which reads as the 8th of a month to everyone using this,
    // and different again on a machine set to another locale. A date nobody can read at a glance
    // without knowing which convention it follows is worse than no date.
    return formatDate(iso)
  }

  return (
    <div className="relative">
      <div className="flex items-center gap-2 flex-wrap">
        <button
          type="button" onClick={save} disabled={busy}
          className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-40"
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
          {currentId.current ? 'Update draft' : 'Save as draft'}
        </button>

        <button
          type="button" onClick={() => setOpen(o => !o)}
          className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50"
        >
          <FileClock className="w-3.5 h-3.5" />
          Drafts{drafts.length > 0 && ` (${drafts.length})`}
          <ChevronDown className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>

        {note && (
          <span className={`text-xs ${note.kind === 'error' ? 'text-red-600' : 'text-green-600'}`}>
            {note.text}
          </span>
        )}
      </div>

      {open && (
        <div className="absolute z-30 mt-2 w-full max-w-md bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden">
          {drafts.length === 0 ? (
            <p className="px-4 py-3 text-xs text-gray-500">
              Nothing saved yet. &ldquo;Save as draft&rdquo; keeps what you have typed so you can
              come back to it — from any device.
            </p>
          ) : (
            <>
              {/* Said once, at the top of the list, where somebody about to resume will read it. */}
              {filesWarning && (
                <p className="px-4 py-2 text-[11px] text-amber-800 bg-amber-50 border-b border-amber-100">
                  Your files are kept on this device. Continue on another one and you will need to
                  attach them again.
                </p>
              )}
              <ul className="max-h-64 overflow-y-auto divide-y divide-gray-100">
                {drafts.map(d => (
                  <li key={d.id} className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50">
                    <button
                      type="button" onClick={() => resume(d)}
                      className="flex-1 text-left min-w-0"
                    >
                      <span className="block text-sm text-gray-900 truncate">{d.label || 'Untitled'}</span>
                      <span className="block text-[11px] text-gray-400">
                        saved {when(d.updated_at)}
                        {currentId.current === d.id && ' · you are editing this one'}
                      </span>
                    </button>
                    <button
                      type="button" onClick={() => remove(d)}
                      title="Delete this draft"
                      className="p-1.5 text-gray-400 hover:text-red-600 rounded-lg shrink-0"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}
    </div>
  )
}
